import { useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { captureFeatureError } from '@/lib/sentry-features'
import { readCookie, deleteCookie } from '@/lib/cookies'
import { IMPERS_STASH_COOKIE, IMPERS_ACTIVE_TAB_KEY } from './storage-keys'
import type { ImpersonationAdminStash } from './types'

/**
 * Handshake de impersonação em nova aba: ?impersonate=TOKEN na URL.
 *
 * A aba ORIGEM (admin) grava IMPERS_STASH_COOKIE e abre window.open com esse
 * param. Aqui a aba DESTINO consome o token e faz session swap.
 *
 * Sem isso, impersonation troca a sessão da própria aba admin, que em
 * cross-subdomain (admin.runnaflow.com → runnaflow.com) não propagava
 * confiavelmente via mirror cookie.
 */
export function useImpersonationHandshake(
  setImpersStash: (s: ImpersonationAdminStash) => void
) {
  useEffect(() => {
    if (typeof window === 'undefined') return
    const params = new URLSearchParams(window.location.search)
    const impersonateToken = params.get('impersonate')
    if (!impersonateToken) return

    // Remove param da URL antes de qualquer await (não vazar em logs/history)
    params.delete('impersonate')
    const newUrl = window.location.pathname +
      (params.toString() ? `?${params.toString()}` : '') +
      window.location.hash
    window.history.replaceState({}, '', newUrl)

    // Admin stash precisa já estar no cookie (aba origem escreveu antes).
    const stashRaw = readCookie(IMPERS_STASH_COOKIE)
    if (!stashRaw) {
      console.warn('[impersonate] admin stash ausente — abortando')
      return
    }

    // Marca ESTA aba como a que está impersonando. Flag per-tab — F5 nessa
    // aba mantém impersonation; aba admin origem (sem flag) continua admin.
    try { sessionStorage.setItem(IMPERS_ACTIVE_TAB_KEY, '1') } catch { /* ignore */ }

    ;(async () => {
      try {
        const { data, error } = await supabase.auth.verifyOtp({
          token_hash: impersonateToken,
          type: 'magiclink',
        })
        if (error || !data.session) {
          console.error('[impersonate] verifyOtp falhou:', error?.message)
          captureFeatureError(error || new Error('verifyOtp returned no session'), 'impersonation', { stage: 'verify_otp' })
          deleteCookie(IMPERS_STASH_COOKIE)
          try { sessionStorage.removeItem(IMPERS_ACTIVE_TAB_KEY) } catch { /* ignore */ }
          alert('Impersonação falhou (token expirado ou inválido). Tente novamente.')
          window.location.href = '/login'
          return
        }
        // Sessão da nova aba agora é do target. onAuthStateChange já dispara
        // SIGNED_IN + loadProfile. Só falta avisar o React do stash pra o
        // banner de impersonação montar (sem esperar re-mount).
        try { setImpersStash(JSON.parse(stashRaw) as ImpersonationAdminStash) } catch { /* ignore */ }
      } catch (err) {
        console.error('[impersonate] erro inesperado:', err)
        captureFeatureError(err, 'impersonation', { stage: 'handshake' })
        deleteCookie(IMPERS_STASH_COOKIE)
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}
